import { useState } from 'react';
import axios from 'axios';
import LayOut from '../components/common/Layout';
import Heading from '../components/common/Heading';
import { APIs } from '../utils/Constants';

export default function Contact() {
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [hasTyped, setHasTyped] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setValidationError('');

    try {
      await axios.post(
        process.env.REACT_APP_UNSPOKEN_SERVICES_API + APIs.message,
        { message },
      );
    } catch (err: any) {
      if (err.response && err.response.data.error) {
        setError(err.response.data.error);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setSubmitted(true);
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setMessage('');
    setError('');
    setValidationError('');
    setSubmitted(false);
    setHasTyped(false);
  };

  const validateMessage = (msg: string) => {
    if (msg.length < 5)
      return 'Message is too short. Minimum 5 characters required.';
    if (msg.length > 1000)
      return 'Message is too long. Maximum 1000 characters allowed.';
    if (msg.toLowerCase().includes('<script>'))
      return 'Invalid content (HTML tags not allowed).';
    return '';
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setMessage(newValue);
    setHasTyped(true);
    setValidationError(validateMessage(newValue));
  };

  return (
    <LayOut layoutStyle="bg-gradient-to-b from-gray-950 via-gray-900 to-gray-800 text-white">
      <div className="pt-16 lg:pt-14">
        {/* Page Header */}
        <div className="text-center px-4 mb-10">
          <Heading text="Reach Out" />
          <p className="text-xs uppercase tracking-widest text-gray-500 mt-1">
            Thoughts, feedback, or just a hello
          </p>
          <div className="mx-auto mt-3 w-10 h-px bg-custom-stone" />
        </div>

        <div className="max-w-lg mx-auto px-4">
          {submitted ? (
            /* Success / Error State */
            <div className="text-center py-10">
              {error ? (
                <>
                  <p className="text-sm text-red-500 mb-6">{error}</p>
                  <button
                    onClick={handleReset}
                    className="inline-flex items-center gap-2 text-sm text-custom-stone border border-gray-700 rounded-full px-5 py-2 hover:border-gray-500 transition-colors duration-200"
                  >
                    <span>&#8592;</span>
                    Re-send message
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-1">
                    Message received.
                  </p>
                  <p className="text-base text-custom-stone font-medium mb-6">
                    Thank you for reaching out.
                  </p>
                  <button
                    onClick={handleReset}
                    className="inline-flex items-center gap-2 text-sm text-custom-stone border border-gray-700 rounded-full px-5 py-2 hover:border-gray-500 transition-colors duration-200"
                  >
                    <span>&#8592;</span>
                    Share more thoughts
                  </button>
                </>
              )}
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <textarea
                value={message}
                onChange={handleChange}
                placeholder="What's on your mind?"
                className={`w-full p-4 text-sm text-gray-200 bg-gray-900 border rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-custom-stone placeholder-gray-600 transition-colors duration-200 ${
                  hasTyped && validationError
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-700 focus:border-custom-stone'
                }`}
                rows={6}
                required
                disabled={isLoading}
              />

              {hasTyped && validationError && (
                <p className="text-xs text-red-500 -mt-2">{validationError}</p>
              )}

              <button
                type="submit"
                disabled={isLoading || !!validationError}
                className={`w-full py-3 rounded-full text-sm font-medium text-white transition-colors duration-200 ${
                  validationError || isLoading
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-custom-coffee hover:bg-custom-coffee/80'
                }`}
              >
                {isLoading ? 'Sending...' : 'Send'}
              </button>

              <p className="text-xs text-gray-400 text-center mt-1">
                Or email{' '}
                <a
                  href="mailto:hello@unspokencreations.com"
                  className="text-custom-stone hover:underline transition-colors duration-200"
                >
                  hello@unspokencreations.com
                </a>
              </p>
            </form>
          )}
        </div>
      </div>
    </LayOut>
  );
}
